import { useAuth } from '../context/AuthContext';
import { researchDepths } from '../lib/researchDepth';
import { Button, EmptyState, FormField, Icon, Tabs } from '../components/ui';
import { useUI } from '../context/UIContext';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, apiError } from '../api/client';
import DirPicker from '../components/DirPicker';
import { useCapabilities } from '../hooks/useCapabilities';
import { statusBadge } from '../lib/format';
import { useAttachmentDraft } from '../hooks/useAttachmentDraft';
import { AttachmentPicker } from '../components/SessionAttachments';
import { newUuid } from '../lib/uuid';
import OutputSelector from '../components/OutputSelector';

const SOURCE_TYPE_LABELS = {
  web_page: 'Веб-страница',
  datasheet: 'Датащит',
  patent: 'Патент',
  paper: 'Статья',
  product_page: 'Продукт',
  repository: 'Репозиторий',
  manual: 'Руководство',
  uploaded_file: 'Файл',
  other: 'Прочее',
};

export default function ProjectPage() {
  const { user } = useAuth();
  const { confirm } = useUI();
  const capabilities = useCapabilities();
  const [error, setError] = useState(null);
  const { projectId } = useParams();
  const attachments = useAttachmentDraft(projectId);
  const startRequestId = useRef(newUuid());
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingSession, setDeletingSession] = useState(null);
  const [activeTab, setActiveTab] = useState('sessions');

  // Workspace path editing
  const [editingWorkspace, setEditingWorkspace] = useState(false);
  const [showDirPicker, setShowDirPicker] = useState(false);
  const [workspacePath, setWorkspacePath] = useState('');
  const [workspaceSubmitting, setWorkspaceSubmitting] = useState(false);

  // Session form
  useEffect(() => {
    let active = true;
    const timer = setInterval(() => { if (document.hidden) return;
      api.getProject(projectId).then(current => { if (active) setProject(current); })
        .catch(() => { console.warn('project.restrictions_refresh_failed'); });
    }, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [projectId]);
  const [showSessionForm, setShowSessionForm] = useState(false);
  const [sessionForm, setSessionForm] = useState({
    prompt: '', title: '', scope: 'research_only', review_level: 'balanced', research_depth: 'standard',
  });
  const [submitting, setSubmitting] = useState(false);
  const [outputs, setOutputs] = useState(null);

  // Knowledge base
  const [kbDocs, setKbDocs] = useState([]);
  const [kbLoading, setKbLoading] = useState(false);
  const [showKbForm, setShowKbForm] = useState(false);
  const [kbMode, setKbMode] = useState('text'); // 'text' | 'file' | 'url'
  const [kbForm, setKbForm] = useState({ title: '', content: '', source_type: 'other', source_url: '' });
  const [kbFile, setKbFile] = useState(null);
  const [kbUrl, setKbUrl] = useState('');
  const [kbSubmitting, setKbSubmitting] = useState(false);

  const load = async () => {
    try {
      const [proj, sess] = await Promise.all([
        api.getProject(projectId),
        api.listSessions(projectId),
      ]);
      setProject(proj);
      setSessions(sess);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadKb = async () => {
    setKbLoading(true);
    try {
      const docs = await api.listKnowledgeBase(projectId);
      setKbDocs(docs);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setKbLoading(false);
    }
  };

  useEffect(() => { load(); }, [projectId]);

  useEffect(() => {
    if (project) setWorkspacePath(project.workspace_path || '');
  }, [project]);

  useEffect(() => {
    if (activeTab !== 'kb') return undefined;
    let stopped = false;
    let timer;
    const poll = async () => {
      await loadKb();
      if (!stopped) timer = setTimeout(poll, 5000);
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [activeTab, projectId]);

  const handleStart = async (e) => {
    e.preventDefault();
    if (!sessionForm.prompt.trim() || !attachments.ready || !outputs?.length) return;
    setSubmitting(true);
    try {
      // Пустая строка в селекте означает «универсальный», то есть «профиль не
      // назван» — а сервер отвергает неизвестный профиль, вместо того чтобы
      // тихо подставить умолчание. Поэтому именно null, а не ''.
      const session = await api.createSession(projectId, {
        ...sessionForm,
        research_depth: sessionForm.scope === 'research_only' ? sessionForm.research_depth : null,
        outputs,
        review_level: sessionForm.review_level,
        attachment_ids: attachments.ids,
        client_request_id: startRequestId.current,
      });
      navigate(`/projects/${projectId}/sessions/${session.id}`);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleWorkspaceSave = async (e) => {
    e.preventDefault();
    setWorkspaceSubmitting(true);
    try {
      const updated = await api.updateProject(projectId, { workspace_path: workspacePath || null });
      setProject(updated);
      setEditingWorkspace(false);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setWorkspaceSubmitting(false);
    }
  };

  const handleKbIngest = async (e) => {
    e.preventDefault();
    setKbSubmitting(true);
    try {
      if (kbMode === 'text') {
        await api.ingestKnowledgeBase(projectId, {
          title: kbForm.title,
          content: kbForm.content,
          source_type: kbForm.source_type,
          source_url: kbForm.source_url || undefined,
        });
      } else if (kbMode === 'file') {
        if (!kbFile) return;
        await api.ingestKnowledgeBaseFile(projectId, kbFile, kbForm.title || undefined);
      } else if (kbMode === 'url') {
        if (!kbUrl.trim()) return;
        await api.ingestKnowledgeBaseUrl(projectId, kbUrl.trim(), kbForm.title || undefined);
      }
      setKbForm({ title: '', content: '', source_type: 'other', source_url: '' });
      setKbFile(null);
      setKbUrl('');
      setShowKbForm(false);
      await loadKb();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setKbSubmitting(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!await confirm(`Удалить проект «${project.title}» и все его сессии? Это действие необратимо.`)) return;
    try { await api.deleteProject(projectId); navigate('/'); }
    catch (err) { setError(apiError(err)); }
  };

  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation();
    if (!await confirm('Удалить сессию? Это действие необратимо.')) return;
    setDeletingSession(sessionId);
    try {
      await api.deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch (err) {
      setError(apiError(err));
    } finally {
      setDeletingSession(null);
    }
  };

  const handleKbDelete = async (docId) => {
    if (!await confirm('Удалить документ из базы знаний?')) return;
    try { await api.deleteKnowledgeBaseDoc(projectId, docId); setKbDocs(prev => prev.filter(d => d.id !== docId)); }
    catch (err) { setError(apiError(err)); }
  };

  const phaseName = (phase) => ({
    intake: 'Сбор', research: 'Исследование', evaluation: 'Оценка',
    architecture: 'Архитектура', critique: 'Критика', repair_cycle: 'Доработка',
    finalization: 'Финализация',
  }[phase] || phase);

  if (loading) return <div className="loading">Загрузка...</div>;
  if (!project && error) return <div role="alert" className="alert alert-error">{error}<Button className="btn" onClick={load}>Повторить</Button></div>;
  if (!project) return <div className="alert alert-error">Проект не найден</div>;

  return (
    <div>
      {project.moderation?.restricted && <div role="status" className="alert alert-error">Проект доступен на чтение. {project.moderation.reasons.join(" ")}</div>}
      <div className="page-header">
        <div>
          <Link to="/" className="text-muted">← Проекты</Link>
          <h1 className="page-title" style={{ marginTop: 4 }}>{project.title}</h1>
          {project.description && <p className="text-muted">{project.description}</p>}
        </div>
        <div className="flex gap-2" style={{ alignItems: 'center' }}>
          {statusBadge(project.status)}
          <Link className="btn" to={`/projects/${projectId}/skills`}>Скиллы проекта</Link>
          <details className="actions-menu"><summary className="icon-button" aria-label="Действия проекта" title="Действия проекта"><Icon name="more" /></summary>
            <div className="actions-menu-panel"><Button className="btn btn-danger" onClick={handleDeleteProject}>Удалить проект</Button></div>
          </details>
        </div>
      </div>

      {error && <div role="alert" className="alert alert-error">{error}</div>}
      {/* Workspace path */}
      {capabilities.workspace_configuration && <div className="card project-workspace" style={{ marginBottom: 20, padding: '12px 16px' }}>
        <div className="flex gap-2" style={{ alignItems: 'center' }}>
          <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Рабочая директория:</span>
          {editingWorkspace ? (
            <form onSubmit={handleWorkspaceSave} className="flex gap-2" style={{ flex: 1, alignItems: 'center' }}><fieldset disabled={project.moderation?.restricted || user?.moderation?.restricted} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
              <code style={{ flex: 1, fontSize: '0.9rem', color: workspacePath ? 'var(--text)' : 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {workspacePath || 'Папка не выбрана'}
              </code>
              <Button type="button" className="btn" onClick={() => setShowDirPicker(true)} style={{ whiteSpace: 'nowrap' }}>
                Выбрать…
              </Button>
              <Button className="btn btn-primary" type="submit" disabled={workspaceSubmitting} style={{ whiteSpace: 'nowrap' }}>
                {workspaceSubmitting ? 'Сохранение...' : 'Сохранить'}
              </Button>
              <Button className="btn" type="button" onClick={() => { setEditingWorkspace(false); setWorkspacePath(project.workspace_path || ''); }}>
                Отмена
              </Button>
            </fieldset></form>
          ) : (
            <>
              <code style={{ flex: 1, color: project.workspace_path ? 'var(--text)' : 'var(--text-muted)', fontSize: '0.9rem' }}>
                {project.workspace_path || 'не задана'}
              </code>
              <Button className="btn" style={{ padding: '2px 12px', fontSize: '0.85rem', whiteSpace: 'nowrap' }} onClick={() => setEditingWorkspace(true)}>
                Изменить
              </Button>
            </>
          )}
        </div>
      </div>

      }
      {capabilities.workspace_configuration && showDirPicker && (
        <DirPicker
          value={workspacePath}
          onSelect={p => setWorkspacePath(p)}
          onClose={() => setShowDirPicker(false)}
        />
      )}

      {/* Tabs */}
      <Tabs label="Разделы проекта" items={[{ key: 'sessions', label: 'Сессии' }, { key: 'kb', label: 'База знаний' }]}
        value={activeTab} onChange={setActiveTab} />

      {/* Sessions tab */}
      {activeTab === 'sessions' && (
        <>
          <div style={{ marginBottom: 16 }}>
            <Button className="btn btn-primary" onClick={() => setShowSessionForm(!showSessionForm)}>
              <Icon name={showSessionForm ? 'close' : 'plus'} size={18} />{showSessionForm ? 'Отмена' : 'Запустить сессию'}
            </Button>
          </div>

          {showSessionForm && (
            <div className="card" style={{ marginBottom: 20 }}>
              <h2 className="card-title" style={{ marginBottom: 16 }}>Новая сессия</h2>
              <form onSubmit={handleStart}><fieldset disabled={project.moderation?.restricted || user?.moderation?.restricted} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                <FormField label={<>Идея / запрос</>}>
                <textarea className="form-control" value={sessionForm.prompt} onChange={e => setSessionForm({
                    ...sessionForm,
                    prompt: e.target.value
                  })} placeholder="Опишите задачу для агентов..." required />
                </FormField>
                <div className="form-group">
                  <FormField label={<>Область работ</>}>
                  <select className="form-control" aria-label="Область работ" value={sessionForm.scope} onChange={e => setSessionForm({
                      ...sessionForm,
                      scope: e.target.value
                    })}>
                                      <option value="research_only">Исследование</option>
                                      <option value="research_and_design">Исследование и проектирование</option>
                                    </select>
                  </FormField>
                  <div className="form-hint" style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    Проектирование включает архитектуру ПО и ПАК, без реализации программ.
                  </div>
                </div>
                {sessionForm.scope === 'research_only' && <FormField label="Глубина исследования"
                  hint={`${researchDepths[sessionForm.research_depth].description} Во всех режимах сохраняются все вопросы запроса.`}>
                  <select className="form-control" aria-label="Глубина исследования" value={sessionForm.research_depth}
                    onChange={e => setSessionForm({ ...sessionForm, research_depth: e.target.value })}>
                    {Object.entries(researchDepths).map(([key, depth]) => <option key={key} value={key}>{depth.label}</option>)}
                  </select>
                </FormField>}
                <details className="advanced-options"><summary>Дополнительные параметры</summary>
                <FormField label={<>Название сессии (опционально)</>}>
                <input className="form-control" value={sessionForm.title} onChange={e => setSessionForm({
                    ...sessionForm,
                    title: e.target.value
                  })} placeholder="Название" />
                </FormField>
                <FormField label="Уровень проверки">
                  <select className="form-control" aria-label="Уровень проверки новой сессии" value={sessionForm.review_level}
                    onChange={e => setSessionForm({ ...sessionForm, review_level: e.target.value })}>
                    <option value="soft">Мягкий</option><option value="balanced">Сбалансированный</option><option value="strict">Строгий</option>
                  </select>
                </FormField>
                </details>
                <OutputSelector projectId={projectId} value={outputs} onChange={setOutputs} disabled={submitting} settings />
                <div className="form-hint" style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                  Методики исследования подбираются автоматически по ходу сессии.
                </div>
                <AttachmentPicker draft={attachments} disabled={submitting} />
                <Button className="btn btn-primary" type="submit" disabled={submitting || !attachments.ready || !outputs?.length}>
                  {submitting ? 'Запуск...' : 'Запустить'}
                </Button>
              </fieldset></form>
            </div>
          )}

          {sessions.length === 0 ? (
            <EmptyState title="Пора начать исследование" action={!showSessionForm && <Button className="btn btn-primary" onClick={() => setShowSessionForm(true)}>Запустить первую сессию</Button>}>
              Опишите задачу — агенты соберут материалы и подготовят результат.
            </EmptyState>
          ) : (
            <div className="grid-2">
              {sessions.map(s => (
                <div
                  key={s.id}
                  className="card session-card"
                >
                  <div className="card-header">
                    <Link className="card-title session-card-link" to={`/projects/${projectId}/sessions/${s.id}`}>{s.title}</Link>
                    <div className="flex gap-2" style={{ alignItems: 'center' }}>
                      {statusBadge(s.status)}
                      {s.status !== 'running' && s.status !== 'waiting_for_human' && (
                        <Button
                          className="btn"
                          style={{ color: 'var(--danger)', background: 'none', border: '1px solid currentColor', padding: '2px 8px', fontSize: '0.8rem' }}
                          disabled={deletingSession === s.id}
                          onClick={(e) => handleDeleteSession(e, s.id)}
                        >
                          {deletingSession === s.id ? '...' : 'Удалить'}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2" style={{ flexWrap: 'wrap' }}>
                    <span className="text-muted">Фаза: {phaseName(s.current_phase)}</span>
                    <span className="text-muted">Итерация: {s.current_iteration}</span>
                    <span className="text-muted">Задач: {s.tasks_created}</span>
                  </div>
                  <p className="text-muted mt-2">{new Date(s.created_at).toLocaleString('ru')}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Knowledge base tab */}
      {activeTab === 'kb' && (
        <>
          <div style={{ marginBottom: 16 }}>
            <p className="text-muted" style={{ marginBottom: 12 }}>
              Документы базы знаний доступны всем агентам при выполнении задач в этом проекте.
            </p>
            <Button className="btn btn-primary" onClick={() => setShowKbForm(!showKbForm)}>
              {showKbForm ? 'Отмена' : '+ Добавить документ'}
            </Button>
          </div>

          {showKbForm && (
            <div className="card" style={{ marginBottom: 20 }}>
              <h2 className="card-title" style={{ marginBottom: 16 }}>Новый документ</h2>

              {/* Mode switcher */}
              <Tabs label="Способ добавления документа" variant="segmented" value={kbMode} onChange={setKbMode}
                items={[{ key: 'text', label: 'Текст' }, { key: 'file', label: 'Файл' }, { key: 'url', label: 'URL' }]} />

              <form onSubmit={handleKbIngest}><fieldset disabled={project.moderation?.restricted || user?.moderation?.restricted} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                <FormField label={<>
                                    Название{kbMode === 'file' || kbMode === 'url' ? ' (опционально)' : ''}
                                  </>}>
                <input className="form-control" value={kbForm.title} onChange={e => setKbForm({
                    ...kbForm,
                    title: e.target.value
                  })} placeholder={kbMode === 'url' ? 'Если пусто — используется URL' : 'Например: Технические требования'} required={kbMode === 'text'} />
                </FormField>

                {kbMode === 'text' && (
                  <>
                    <FormField label={<>Тип источника</>}>
                    <select className="form-control" value={kbForm.source_type} onChange={e => setKbForm({
                        ...kbForm,
                        source_type: e.target.value
                      })}>
                                            {Object.entries(SOURCE_TYPE_LABELS).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                                          </select>
                    </FormField>
                    <FormField label={<>URL источника (опционально)</>}>
                    <input className="form-control" value={kbForm.source_url} onChange={e => setKbForm({
                        ...kbForm,
                        source_url: e.target.value
                      })} placeholder="https://..." />
                    </FormField>
                    <FormField label={<>Содержимое</>}>
                    <textarea className="form-control" value={kbForm.content} onChange={e => setKbForm({
                        ...kbForm,
                        content: e.target.value
                      })} placeholder="Вставьте текст документа..." rows={10} required />
                    </FormField>
                  </>
                )}

                {kbMode === 'file' && (
                  <div className="form-group">
                    <FormField label={<>
                                          Файл (PDF, DOCX, PPTX, XLSX, MD, TXT, изображения…)
                                        </>}>
                    <input type="file" className="form-control" onChange={e => setKbFile(e.target.files[0] || null)} accept=".pdf,.docx,.pptx,.xlsx,.xls,.md,.txt,.html,.csv,.json,.png,.jpg,.jpeg,.webp" required />
                    </FormField>
                    {kbFile && (
                      <p className="text-muted" style={{ marginTop: 6, fontSize: '0.85rem' }}>
                        {kbFile.name} — {(kbFile.size / 1024).toFixed(1)} KB
                      </p>
                    )}
                  </div>
                )}

                {kbMode === 'url' && (
                  <div className="form-group">
                    <FormField label={<>URL страницы</>}>
                    <input className="form-control" value={kbUrl} onChange={e => setKbUrl(e.target.value)} placeholder="https://example.com/page" type="url" required />
                    </FormField>
                    <p className="text-muted" style={{ marginTop: 6, fontSize: '0.85rem' }}>
                      Страница будет открыта в браузере (Playwright) — работает с JS-сайтами
                    </p>
                  </div>
                )}

                <Button className="btn btn-primary" type="submit" disabled={kbSubmitting}>
                  {kbSubmitting
                    ? (kbMode === 'url' ? 'Загрузка страницы...' : 'Обработка...')
                    : 'Добавить в базу знаний'}
                </Button>
              </fieldset></form>
            </div>
          )}

          {kbLoading ? (
            <div className="loading">Загрузка...</div>
          ) : kbDocs.length === 0 ? (
            <div className="empty">База знаний пуста. Добавьте первый документ!</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {kbDocs.map(doc => (
                <div key={doc.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{doc.title}</div>
                    <div role="status">{({ pending: 'Ожидает индексации', indexing: 'Индексируется', ready: 'Готов к поиску', failed: 'Ошибка индексации' })[doc.index_status] || 'Статус неизвестен'}</div>
                    {doc.index_status === 'failed' && <Button className="btn" onClick={() => api.reindexDocument(projectId, doc.id).then(loadKb).catch(err => setError(apiError(err)))}>Повторить индексацию</Button>}
                    <div className="flex gap-2">
                      <span className="badge badge-created">{SOURCE_TYPE_LABELS[doc.source_type] || doc.source_type}</span>
                      {doc.source_url && (
                        <a href={doc.source_url} target="_blank" rel="noopener noreferrer" className="text-muted" style={{ fontSize: '0.8rem', overflowWrap: 'anywhere' }}>
                          {doc.source_url.length > 50 ? doc.source_url.slice(0, 50) + '...' : doc.source_url}
                        </a>
                      )}
                    </div>
                    <p className="text-muted mt-2" style={{ fontSize: '0.8rem' }}>
                      {new Date(doc.created_at).toLocaleString('ru')}
                    </p>
                  </div>
                  <Button
                    className="btn"
                    style={{ color: 'var(--danger)', background: 'none', border: '1px solid currentColor', padding: '4px 10px', fontSize: '0.85rem' }}
                    onClick={() => handleKbDelete(doc.id)}
                  >
                    Удалить
                  </Button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
